import * as React from 'react';
import {useSelector} from 'react-redux';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Assignables} from 'nutshell-core/assignables';
import {
    safelyGetFilterByKey,
    safelyGetRelationshipFilterIdsFromFilterValue,
} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {SaveButton} from 'shells/button/save-button/save-button';
import {LAST_UPDATED_VALUE_OPTIONS} from 'shells/control-bar/constants';
import {ExpandedFilterBar} from 'shells/control-bar/expanded-filter-bar';
import {getEditionStatusesForQuery, getLastUpdatedValue} from 'shells/control-bar/helpers';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {ViewBoardIcon as BoardIcon} from 'shells/icon/svg/view-board-icon';
import {ViewListIcon as ListIcon} from 'shells/icon/svg/view-list-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {PageContainer, PageTitleBar} from '../../../layout';

import {appHistory} from '../../../../history';
import type {NutshellState} from '../../../../store';
import {writeLocalStorage} from '../../../../util/localStorage';

import {BroadcastsBoardView} from './broadcasts-board-view';
import {BroadcastsTableView} from './broadcasts-table-view';
import {
    useCreateSmsBroadcast,
    useGetSmsBroadcasts,
    useGetSmsBroadcastEngagementStats,
} from './graphql/hooks';

const SMS_BROADCAST_VIEW_SEARCH = 'SMS_BROADCAST_VIEW_SEARCH';

type Props = ContextRouter & {
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    activeFilters: FilterObject[];
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
    onParamChange: (arg1: any, arg2: boolean) => any;
};

export function BroadcastsListPage(props: Props) {
    const {createBroadcast, isCreating} = useCreateSmsBroadcast();
    const archived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';
    const statuses = getEditionStatusesForQuery(props.activeFilters);
    const isFiltering = Boolean(props.activeFilters.length);
    const assignables = useSelector((state: NutshellState) => Assignables.getAll(state));
    const ownerFilter = safelyGetFilterByKey(props.activeFilters, 'owner');
    const assigneeIds = ownerFilter
        ? safelyGetRelationshipFilterIdsFromFilterValue(ownerFilter)
        : [];
    const ownerFilterProps = {
        ownerFilter: ownerFilter,
        collection: assignables,
    } as const;
    const lastUpdatedTime = getLastUpdatedValue(props.activeFilters);
    const isFilteringOnCreatorUserId = Boolean(
        props.activeFilters.find((filter: FilterObject) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );
    const {user} = useGetSessionUser();
    const creatorUserId = isFilteringOnCreatorUserId && user ? user.id : undefined;
    const search = props.urlParams.q || '';

    const queryVariables = {
        archived,
        statuses,
        assigneeIds,
        lastUpdatedTime,
        creatorUserId,
        search,
    };
    const {
        smsBroadcasts,
        isLoading: isLoadingBroadcastList,
        error: broadcastsListError,
    } = useGetSmsBroadcasts(queryVariables);

    const {engagementStatsMap, isLoadingEngagementStats} =
        useGetSmsBroadcastEngagementStats(queryVariables);

    const onCreateBroadcast = (): Promise<void> => {
        return createBroadcast().then((result) => {
            if (
                result &&
                result.data &&
                result.data.smsBroadcastCreate &&
                result.data.smsBroadcastCreate.broadcast &&
                result.data.smsBroadcastCreate.broadcast.edition.htmlUrlPath
            ) {
                appHistory.push(result.data.smsBroadcastCreate.broadcast.edition.htmlUrlPath);
            }
        });
    };

    const selectedTab = props.urlParams.view || 'board';

    const shouldShowLoading = isLoadingBroadcastList && !smsBroadcasts;
    const tabOptions = [
        {
            label: 'Board',
            id: 'board',
            icon: BoardIcon,
            href: props.getUpdatedUrl({view: 'board'}),
            component: (
                <BroadcastsBoardView
                    isLoading={shouldShowLoading}
                    error={broadcastsListError}
                    smsBroadcasts={smsBroadcasts || []}
                    onCreateBroadcast={onCreateBroadcast}
                    isCreating={isCreating}
                    isFiltering={isFiltering}
                    queryVariables={queryVariables}
                />
            ),
        },
        {
            label: 'Table',
            id: 'table',
            icon: ListIcon,
            href: props.getUpdatedUrl({view: 'table'}),
            component: (
                <BroadcastsTableView
                    isLoading={shouldShowLoading}
                    error={broadcastsListError}
                    broadcasts={smsBroadcasts || []}
                    engagementStatsMap={engagementStatsMap}
                    isLoadingEngagementStats={isLoadingEngagementStats}
                    onCreateBroadcast={onCreateBroadcast}
                    isCreating={isCreating}
                    isFiltering={isFiltering}
                    sortString={props.urlParams.sort}
                    onChangeSort={(newSortString) => {
                        props.onParamChange({sort: newSortString}, true);
                    }}
                />
            ),
        },
    ];

    React.useEffect(() => {
        writeLocalStorage(SMS_BROADCAST_VIEW_SEARCH, props.location.search);
    }, [props.location.search]);

    return (
        <PageContainer
            title='SMS broadcasts'
            pageTitleBar={
                <PageTitleBar
                    title='SMS broadcasts'
                    subheaderPrimaryText='Reach out to thousands at a time with one quick message'
                    buttons={
                        <SaveButton
                            variant='primary'
                            size='big'
                            onClick={onCreateBroadcast}
                            isSaving={isCreating}
                            buttonText='Create new broadcast'
                            savingText='Creating broadcast'
                            successText='Broadcast created'
                        />
                    }
                />
            }
            controlBar={
                <ListPageControlBar
                    archiveTab={{
                        isActive: archived,
                        onChange: () => {
                            props.onUpdateFilters({
                                archived: archived ? null : true,
                            });
                        },
                    }}
                    tabs={{
                        selectedTab: selectedTab,
                        options: tabOptions,
                    }}
                    leftSection={
                        user && (
                            <ExpandedFilterBar
                                ownerFilterProps={
                                    user.permissions.canAccessFullCampaigns
                                        ? ownerFilterProps
                                        : undefined
                                }
                                statusFilterProps={{
                                    statusFilter: safelyGetFilterByKey(
                                        props.activeFilters,
                                        'status'
                                    ),
                                    statusOptions: ['Draft', 'Scheduled', 'Sent'],
                                    statusDisabled: archived,
                                }}
                                lastUpdatedFilterProps={{
                                    lastUpdatedFilter: safelyGetFilterByKey(
                                        props.activeFilters,
                                        'lastUpdated'
                                    ),
                                    // @ts-expect-error
                                    lastUpdatedOptions: LAST_UPDATED_VALUE_OPTIONS,
                                }}
                                justMineFilterProps={{
                                    isFilteringOnCreatorUserId: isFilteringOnCreatorUserId,
                                    userId: user.id,
                                }}
                                searchFilterProps={{
                                    searchValue: search,
                                    onUpdateSearch: (newSearchValue) => {
                                        props.onParamChange({q: newSearchValue}, true);
                                    },
                                }}
                                isFiltering={isFiltering}
                                noun='broadcast'
                                count={smsBroadcasts?.length || 0}
                                onUpdateFilters={props.onUpdateFilters}
                                onRemoveAllFilters={props.onRemoveAllFilters}
                                activeFilters={props.activeFilters}
                            />
                        )
                    }
                />
            }
        >
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
        </PageContainer>
    );
}
